import React from 'react';
import { StarOutlineIcon, StarSolidIcon } from './Icons';

/**
 * Deux rendus d'étoiles coexistent dans la maquette (piège n°20) :
 * des glyphes « ★★★★☆ » dans les cartes, et cinq SVG (pleins/contour) dans
 * le widget Goodays. Même logique de remplissage dans les deux cas.
 */

/**
 * Glyphes texte — `.stars`. Le libellé accessible dit la note en clair.
 *
 * `as` reproduit l'élément de la maquette, qui n'est pas le même selon le
 * contexte : `<div class="stars">` dans les cartes d'avis (détail 294-309,
 * listing 349-364), `<span class="stars">` dans `.salon-row-rating`
 * (listing 139-254). Ce n'est pas cosmétique : sur un inline, le
 * `margin-bottom: 7px` de `.avis .stars` (base.css:432) est sans effet.
 */
export const StarsText = ({ rating = 0, className = 'stars', as: Tag = 'span' }) => {
  const filled = Math.round(parseFloat(rating) || 0);
  const glyphs = '★'.repeat(Math.min(filled, 5)) + '☆'.repeat(Math.max(0, 5 - filled));
  return (
    <Tag className={className} aria-hidden="true">
      {glyphs}
    </Tag>
  );
};

const OUTLINE_STAR_PROPS = { stroke: 'var(--jaune)', strokeWidth: 1.5, scale: 0.9 };

/**
 * Une étoile, remplie à hauteur de `fraction` (0 à 1). Pour un remplissage
 * partiel, l'étoile pleine est superposée au contour et rognée en largeur —
 * 3.5/5 doit se voir comme 3 pleines + 1 demie, pas être arrondie à 4.
 */
const PartialStarSvg = ({ fraction, size }) => {
  if (fraction <= 0) return <StarOutlineIcon size={size} {...OUTLINE_STAR_PROPS} />;
  if (fraction >= 1) return <StarSolidIcon size={size} />;
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: size, height: size, flex: '0 0 auto' }}>
      <StarOutlineIcon size={size} {...OUTLINE_STAR_PROPS} style={{ position: 'absolute', inset: 0 }} />
      <span style={{ position: 'absolute', inset: 0, overflow: 'hidden', width: `${fraction * 100}%` }}>
        <StarSolidIcon size={size} />
      </span>
    </span>
  );
};

/** Cinq étoiles SVG — `.goodays-widget-stars`. Remplissage proportionnel à `rating`. */
export const StarsSvg = ({ rating = 0, size = 26 }) => {
  const value = Math.min(5, Math.max(0, parseFloat(rating) || 0));
  return (
    <div className="goodays-widget-stars" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((index) => (
        <PartialStarSvg key={index} size={size} fraction={value - index} />
      ))}
    </div>
  );
};

export default StarsText;
